import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useHeatmapEvents, useFeatureFlag, bucketEvents } from "@/lib/analytics";
import { Flame, Activity, AlertTriangle, MapPin, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/analytics/")({
  component: AnalyticsOverview,
});

function AnalyticsOverview() {
  const { currentOrg } = useAuth();
  const enabled = useFeatureFlag("advanced_analytics");
  const since = useMemo(() => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(), []);
  const { data: events = [], isLoading } = useHeatmapEvents({ fromDate: since, limit: 5000 });

  if (!enabled.isLoading && !enabled.data) {
    return <Locked />;
  }

  const incidents = events.filter((e) => e.source === "incident").length;
  const breaches = events.filter((e) => e.source === "geofence" && e.event_type.includes("breach")).length;
  const scans = events.filter((e) => e.source === "scan").length;
  const gps = events.filter((e) => e.source === "gps").length;
  const buckets = bucketEvents(events, 3);
  const hotspots = [...buckets].sort((a, b) => b.weight_total - a.weight_total).slice(0, 5);

  const plan = currentOrg?.plan ?? "trial";
  const retentionLabel = plan === "enterprise" ? "Unlimited retention" : plan === "pro" ? "30-day retention" : "Limited";

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Flame} label="Geo events (7d)" value={isLoading ? "…" : events.length.toString()} accent="text-primary" />
        <Stat icon={AlertTriangle} label="Incidents" value={incidents.toString()} accent="text-destructive" />
        <Stat icon={MapPin} label="Geofence breaches" value={breaches.toString()} accent="text-warning" />
        <Stat icon={Activity} label="GPS / scans" value={`${gps} · ${scans}`} accent="text-success" />
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Top hotspots (7d)</h3>
          <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">{retentionLabel}</span>
        </div>
        {hotspots.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No geo activity yet. Heatmaps populate as patrols, scans and incidents flow in.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {hotspots.map((h, i) => (
              <li key={`${h.bucket_lat}-${h.bucket_lng}`} className="flex items-center justify-between py-3 text-sm">
                <span className="font-mono text-xs text-muted-foreground">#{i + 1} · {h.bucket_lat.toFixed(3)}, {h.bucket_lng.toFixed(3)}</span>
                <span><b>{h.event_count}</b> events · <b className="text-destructive">{h.incident_count}</b> incidents · weight {h.weight_total.toFixed(1)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <NavCard to="/analytics/heatmaps" icon={Flame} title="Heatmaps" desc="Density overlays for movement and incidents." />
        <NavCard to="/analytics/activity" icon={Activity} title="Activity density" desc="Patrol coverage and blind spots." />
        <NavCard to="/analytics/incidents" icon={AlertTriangle} title="Incident analytics" desc="Severity, hotspots, repeat zones." />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }: { icon: any; label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <Icon className={`h-4 w-4 ${accent ?? "text-muted-foreground"}`} />
      <div className="mt-2 text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function NavCard({ to, icon: Icon, title, desc }: { to: any; icon: any; title: string; desc: string }) {
  return (
    <Link to={to} className="group rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/40">
      <Icon className="h-4 w-4 text-primary" />
      <h4 className="mt-2 font-display text-base font-semibold">{title}</h4>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Open <ArrowRight className="h-3 w-3" />
      </span>
    </Link>
  );
}

export function Locked() {
  return (
    <div className="rounded-xl border border-warning/40 bg-warning/5 p-8 text-center">
      <Lock className="mx-auto h-8 w-8 text-warning" />
      <h3 className="mt-3 font-display text-xl font-semibold">Advanced Analytics is disabled</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Heatmaps, density maps and zone analytics are part of the Advanced Analytics module.
        Owners can enable it under <Link to="/modules" className="text-primary underline">Modules</Link>.
      </p>
    </div>
  );
}
